import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, BellRing, Plus, Send, PhoneCall, FileText, UserCircle2, ArrowLeft } from "lucide-react";

export default function WebexPanel({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [roomId, setRoomId] = useState("");
  const [transcript, setTranscript] = useState(null);

  const { data: status } = useQuery({
    queryKey: ["webex-status"],
    queryFn: async () => (await api.get("/webex/status")).data,
    enabled: open,
  });
  const { data: rooms } = useQuery({
    queryKey: ["webex-rooms"],
    queryFn: async () => (await api.get("/webex/rooms")).data,
    enabled: open && !!status?.connected,
  });
  const { data: calls, isLoading: callsLoading } = useQuery({
    queryKey: ["webex-calls"],
    queryFn: async () => (await api.get("/webex/calls")).data,
    enabled: open && !!status?.connected,
  });
  const { data: transcripts, isLoading: trLoading } = useQuery({
    queryKey: ["webex-transcripts"],
    queryFn: async () => (await api.get("/webex/transcripts")).data,
    enabled: open && !!status?.connected,
  });

  const saveRoom = useMutation({
    mutationFn: async () => {
      const title = (rooms?.items || []).find((r) => r.id === roomId)?.title || "";
      return (await api.post("/webex/notify-room", { room_id: roomId, title })).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["webex-status"] }); toast.success("Notification space saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to save"),
  });
  const createRoom = useMutation({
    mutationFn: async () => (await api.post("/webex/rooms/create")).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["webex-status"] });
      qc.invalidateQueries({ queryKey: ["webex-rooms"] });
      toast.success(`Space "${d.room?.title}" created & set as alerts space`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to create space"),
  });
  const testMsg = useMutation({
    mutationFn: async () => (await api.post("/webex/test-message")).data,
    onSuccess: () => toast.success("Test alert sent — check Webex"),
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send"),
  });

  const openTranscript = async (t) => {
    try {
      const { data } = await api.get(`/webex/transcripts/${t.id}/content`);
      setTranscript({ ...t, content: data.content });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load transcript");
    }
  };

  const activeRoomId = roomId || status?.notify_room?.id || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="webex-panel-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Webex — Manage</DialogTitle>
        </DialogHeader>

        {!status?.connected ? (
          <p className="text-sm text-slate-600">Webex is not connected. Close this and click "Connect with Webex".</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 border border-slate-200 bg-slate-50 p-3 rounded-sm" data-testid="webex-account-row">
              <UserCircle2 size={26} className="text-violet-600" />
              <div className="text-sm">
                <div className="font-semibold text-slate-900">{status.account?.display_name || "Webex account"}</div>
                <div className="text-slate-500 text-xs">{status.account?.email}</div>
              </div>
              <Badge variant="outline" className="ml-auto rounded-sm border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]">LIVE OAuth</Badge>
            </div>

            <div className="border border-slate-200 p-3 rounded-sm space-y-2">
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><BellRing size={14} className="text-violet-600" /> Team alert space</div>
              <p className="text-xs text-slate-500">New enquiries and quote acceptances post here automatically.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <Select value={activeRoomId} onValueChange={setRoomId}>
                  <SelectTrigger className="rounded-sm w-64" data-testid="webex-room-select">
                    <SelectValue placeholder="Pick a Webex space" />
                  </SelectTrigger>
                  <SelectContent>
                    {(rooms?.items || []).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.title || "(untitled space)"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button data-testid="webex-save-room-btn" size="sm" onClick={() => saveRoom.mutate()} disabled={!roomId || saveRoom.isPending} className="rounded-sm bg-violet-600 hover:bg-violet-700">
                  {saveRoom.isPending ? <Loader2 size={13} className="animate-spin" /> : "Save"}
                </Button>
                <Button data-testid="webex-create-room-btn" size="sm" variant="outline" onClick={() => createRoom.mutate()} disabled={createRoom.isPending} className="rounded-sm">
                  {createRoom.isPending ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} className="mr-1" /> Create "CCFI - Compass Alerts"</>}
                </Button>
                <Button data-testid="webex-test-msg-btn" size="sm" variant="outline" onClick={() => testMsg.mutate()} disabled={testMsg.isPending || !status?.notify_room} className="rounded-sm">
                  {testMsg.isPending ? <Loader2 size={13} className="animate-spin" /> : <><Send size={13} className="mr-1" /> Send test</>}
                </Button>
              </div>
              {status?.notify_room && <div className="text-xs text-emerald-700">Current: <b>{status.notify_room.title || status.notify_room.id}</b></div>}
            </div>

            <Tabs defaultValue="calls">
              <TabsList className="rounded-sm">
                <TabsTrigger value="calls" data-testid="webex-tab-calls" className="rounded-sm text-xs"><PhoneCall size={13} className="mr-1.5" /> Recent calls</TabsTrigger>
                <TabsTrigger value="transcripts" data-testid="webex-tab-transcripts" className="rounded-sm text-xs"><FileText size={13} className="mr-1.5" /> Meeting transcripts</TabsTrigger>
              </TabsList>

              <TabsContent value="calls" className="max-h-56 overflow-y-auto border border-slate-200 rounded-sm">
                {callsLoading ? <div className="p-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                  : calls?.warning ? <div className="p-4 text-xs text-amber-700 bg-amber-50">{calls.warning}</div>
                  : (calls?.items || []).length === 0 ? <div className="p-4 text-xs text-slate-500">No calls yet.</div>
                  : (calls.items).map((c) => (
                      <div key={c.id} className="px-3 py-2 border-b border-slate-100 last:border-0 text-xs flex items-center gap-3">
                        <Badge variant="outline" className={`rounded-sm text-[9px] ${c.call_type === "missed" ? "border-red-300 bg-red-50 text-red-700" : "border-slate-300"}`}>{c.call_type || "call"}</Badge>
                        <div className="font-medium text-slate-800">{c.name || c.number || "Unknown"}</div>
                        <div className="text-slate-500">{c.number}</div>
                        <div className="ml-auto text-slate-400">{c.start_time ? new Date(c.start_time).toLocaleString("en-AU") : ""}</div>
                      </div>
                    ))}
              </TabsContent>

              <TabsContent value="transcripts" className="max-h-56 overflow-y-auto border border-slate-200 rounded-sm">
                {transcript ? (
                  <div className="p-3">
                    <Button size="sm" variant="outline" onClick={() => setTranscript(null)} className="rounded-sm mb-2"><ArrowLeft size={13} className="mr-1" /> Back</Button>
                    <div className="text-xs font-semibold text-slate-900 mb-1">{transcript.meeting_topic}</div>
                    <pre className="text-[11px] whitespace-pre-wrap text-slate-700 bg-slate-50 p-2 rounded-sm max-h-36 overflow-y-auto">{transcript.content}</pre>
                  </div>
                ) : trLoading ? <div className="p-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                  : transcripts?.warning ? <div className="p-4 text-xs text-amber-700 bg-amber-50">{transcripts.warning}</div>
                  : (transcripts?.items || []).length === 0 ? <div className="p-4 text-xs text-slate-500">No transcripts yet — record a Webex meeting with transcription on.</div>
                  : (transcripts.items).map((t) => (
                      <button key={t.id} onClick={() => openTranscript(t)} className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 text-xs hover:bg-violet-50 transition-colors" data-testid="webex-transcript-row">
                        <div className="font-medium text-slate-800">{t.meeting_topic || "(untitled meeting)"}</div>
                        <div className="text-slate-400">{t.start_time ? new Date(t.start_time).toLocaleString("en-AU") : ""}</div>
                      </button>
                    ))}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
